import { findTermRanges } from '../SplContentRenderer';
/**
 * BilingualPdfViewerEmbed — the [ PDF page viewer | English side panel ] layout
 * shared by the bilingual PDF families (zh_pdf, ch_pdf).  Hovering a source
 * block dims the page, highlights the block and streams its English into the
 * side panel — the translation NEVER covers the source (the page image stays
 * the truth).  Each family supplies a ``BilingualViewerConfig`` (viewer API,
 * translate call, source label, query script); see ZhPdfViewerEmbed /
 * ChPdfViewerEmbed.  Extracted from ZhPdfViewerEmbed so the families share
 * one copy.
 */
import { forwardRef, useImperativeHandle, useRef, useState, useEffect, useCallback, type FC, type ReactNode } from 'react';
import type { ChunkMetadata, TOCEntry } from '../../types/viewer';
import type { JpHoverRegion } from './PageRenderer';
import type { createViewerApi } from '../../api/createViewerApi';
import RegulatoryViewerEmbed, {
  type ChunkAnchor,
  type DocSearchMode,
  type RegulatoryViewerHandle,
} from './RegulatoryViewerEmbed';

export type BilingualPdfViewerHandle = RegulatoryViewerHandle;

export interface BilingualViewerConfig {
  viewerApi: ReturnType<typeof createViewerApi>;
  /** source block -> English (the family's /translate). */
  translate: (text: string, preserveHtml: boolean, docId: number,
              sectionId?: number | null) => Promise<string>;
  /** Label of the collapsible source block, e.g. "中文 (source)". */
  sourceLabel: string;
  /** Source-language name in hints, e.g. "Chinese" / "original". */
  sourceName: string;
  /** Script of the source language. When set, a query in that script is boxed
   *  on the page and an English query only in the panel (zh: Han). When unset
   *  (Latin sources — German vs English cannot be told apart by script), the
   *  query is boxed on the page AND marked in the panel. */
  sourceScriptRe?: RegExp;
  /** Region field holding box-OCR source text on scanned pages. */
  regionTextField?: 'text_ja' | 'text_zh';
  /** TOC field carrying the source-language section title. */
  titleSrcField: 'title_zh' | 'title_src';
  /** In-document search: ``'byScript'`` routes a source-script query to the
   *  page text and anything else to the English (zh, JP); ``'both'`` searches
   *  the page text AND the English together (Latin sources: de/fr/it). */
  docSearch: DocSearchMode;
  /** docSearch='both': English search term → source term (``/translate-term``)
   *  so an English query also finds the untranslated page text. */
  translateTerm?: (en: string, docId: number) => Promise<string>;
}

/**
 * FormattedBlock — render a flattened block translation with structure,
 * keying off markers that survive translation. Conventions observed
 * across the doc types:
 *   ●/○        sub-headings                -> bold sub-heading
 *   ・          bullets                     -> bullet
 *   【…】/〔…〕  section titles              -> bold heading
 *   "1. " "(1) " numbered headings          -> bold lead (needs trailing
 *               space so values like 1.2mg are NOT mis-split)
 * Degrades to plain paragraphs when a block has none, so any doc type is
 * safe. Block boundaries are the unambiguous markers only — numbers are
 * matched at a segment START, never split mid-text.
 */
const _NUM_HEAD = /^(\d+[．.]\s|[（(]\d+[）)]\s|\d+[）)]\s)(.*)$/;

// Wrap every case-insensitive occurrence of ``term`` in ``text`` with a
// <mark> so the searched English word is highlighted in the translation
// panel (so the eye lands on it instead of hunting). Returns the raw string
// untouched when there's no term/match. ``split`` with a capturing group
// alternates [text, match, text, match, …] → odd indices are the matches.
function highlightTerm(text: string, term?: string | string[] | null, englishExact = false): ReactNode {
  if (englishExact) {
    const terms = Array.isArray(term) ? term : term ? [term] : [];
    const ranges = findTermRanges(text, terms, true);
    const nodes: ReactNode[] = [];
    let pos = 0;
    ranges.forEach((r, i) => {
      nodes.push(text.slice(pos, r.start), <mark key={i} className="bg-yellow-200 rounded-sm px-0.5">{text.slice(r.start, r.end)}</mark>);
      pos = r.end;
    });
    nodes.push(text.slice(pos));
    return nodes;
  }
  const ts = (Array.isArray(term) ? term : [term ?? '']).map((t) => t.trim()).filter(Boolean);
  if (!ts.length || !text) return text;
  const esc = ts.map((t) => t.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')).join('|');
  const parts = text.split(new RegExp(`(${esc})`, 'gi'));
  if (parts.length === 1) return text;
  return parts.map((p, i) =>
    i % 2 === 1
      ? <mark key={i} className="bg-yellow-200 rounded-sm px-0.5">{p}</mark>
      : p,
  );
}

const FormattedBlock: FC<{ text: string; highlight?: string | string[] | null; englishExact?: boolean }> = ({ text, highlight, englishExact }) => {
  const hl = (s: string): ReactNode => highlightTerm(s, highlight, englishExact);
  const parts = text
    // The model emits <br> for line breaks; treat them (and literal \n) as breaks
    // so multi-line regions render as lines instead of a run-on with visible <br>.
    .replace(/<br\s*\/?>/gi, '\n')
    .replace(/([●○・【〔])/g, '\n$1')
    .split('\n')
    .map((s) => s.trim())
    .filter(Boolean);
  return (
    <div className="space-y-1 text-sm leading-relaxed">
      {parts.map((p, i) => {
        if (p[0] === '●' || p[0] === '○') {
          const body = p.replace(/^[●○]\s*/, '');
          const m = body.match(/^(.{2,48}?[:：])\s*([\s\S]*)$/);
          return m ? (
            <p key={i} className="mt-2 text-slate-800">
              <span className="font-semibold">{hl(m[1] ?? '')}</span> {hl(m[2] ?? '')}
            </p>
          ) : (
            <p key={i} className="mt-2 font-semibold text-slate-800">{hl(body)}</p>
          );
        }
        if (p[0] === '【' || p[0] === '〔') {
          return <p key={i} className="mt-2 font-semibold text-slate-800">{hl(p)}</p>;
        }
        if (p[0] === '・') {
          return <p key={i} className="pl-3 text-slate-700">• {hl(p.replace(/^・\s*/, ''))}</p>;
        }
        const nm = p.match(_NUM_HEAD);
        if (nm) {
          return (
            <p key={i} className="mt-1.5 text-slate-800">
              <span className="font-semibold">{(nm[1] ?? '').trim()}</span> {hl(nm[2] ?? '')}
            </p>
          );
        }
        return <p key={i} className="text-slate-700">{hl(p)}</p>;
      })}
    </div>
  );
};

/**
 * LinkedTables — render the translated grid and the Chinese source grid one
 * above the other, with SYNCHRONIZED cell highlighting: hovering a cell in
 * either table highlights the SAME logical cell in both.  The two tables share
 * an identical cell structure (the backend's cell-wise translation rebuilds the
 * grid from the source's cells, so the Nth cell of each is the same logical
 * cell), so a single reading-order index links them.  This is the "highlight
 * the translation as in the original" the reviewer asked for.
 */
type Cell = { tag: 'td' | 'th'; text: string; colspan: number };

function parseGrid(html: string): Cell[][] {
  try {
    const doc = new DOMParser().parseFromString(html, 'text/html');
    return [...doc.querySelectorAll('tr')].map((tr) =>
      [...tr.children].map((td) => ({
        tag: (td.tagName.toLowerCase() === 'th' ? 'th' : 'td') as 'td' | 'th',
        text: td.textContent || '',
        colspan: parseInt(td.getAttribute('colspan') || '1', 10) || 1,
      })),
    );
  } catch {
    return [];
  }
}

const LinkedTables: FC<{ enHtml: string; zhHtml: string; sourceLabel: string; highlight?: string | string[] | null; englishExact?: boolean }> = ({
  enHtml,
  zhHtml,
  sourceLabel,
  highlight,
  englishExact,
}) => {
  const [hover, setHover] = useState<number | null>(null);
  const en = parseGrid(enHtml);
  const zh = parseGrid(zhHtml);
  // The two grids must share the same cell count for index-linking to be
  // valid; if a translation fell back to the generic path and diverged, drop
  // the linkage (still render the translated grid, just unlinked).
  const enCount = en.reduce((n, r) => n + r.length, 0);
  const zhCount = zh.reduce((n, r) => n + r.length, 0);
  const linked = enCount > 0 && enCount === zhCount;

  // ``hl`` applies the search-term <mark> to cell text (English grid only — the
  // searched term is English; the Chinese source grid stays plain). Without it,
  // a term that matched the table in search was invisible in the rendered grid.
  const renderGrid = (rows: Cell[][], hl?: string | string[] | null) => {
    let idx = -1;
    return (
      <table>
        <tbody>
          {rows.map((r, ri) => (
            <tr key={ri}>
              {r.map((c, ci) => {
                idx += 1;
                const my = idx;
                const Tag = c.tag;
                const on = linked && hover === my;
                return (
                  <Tag
                    key={ci}
                    colSpan={c.colspan}
                    onMouseEnter={linked ? () => setHover(my) : undefined}
                    style={on ? { background: 'rgba(16,185,129,0.28)' } : undefined}
                  >
                    {hl ? highlightTerm(c.text, hl, englishExact) : c.text}
                  </Tag>
                );
              })}
            </tr>
          ))}
        </tbody>
      </table>
    );
  };

  return (
    <div onMouseLeave={() => setHover(null)} className="space-y-2">
      <div className="zh-pdf-table text-sm text-slate-800">{renderGrid(en, highlight)}</div>
      <details open className="mt-1">
        <summary className="text-[11px] text-slate-400 cursor-pointer">
          {sourceLabel}{linked ? ' — hover to link cells' : ''}
        </summary>
        <div className="zh-pdf-table mt-1 text-xs text-slate-500">{renderGrid(zh)}</div>
      </details>
    </div>
  );
};

export interface BilingualPdfViewerEmbedProps {
  docId: number;
  highlightChunkIds?: number[];
  chunkAnchors?: Record<number, ChunkAnchor[]>;
  /** page_num -> anchor for a chunk-free (progressive-read) citation.
   *  Forwarded verbatim to RegulatoryViewerEmbed via the props spread. */
  pageAnchors?: Record<number, ChunkAnchor[]> | undefined;
  landingPage?: number | undefined;
  highlightTerm?: string | null;
  onChunkClick?: (chunk: ChunkMetadata) => void;
  /** Compare view: colored keyword terms + section page-filter + TOC callback,
   *  forwarded to RegulatoryViewerEmbed (terms merged with the search term). */
  highlightTerms?: string[] | null;
  englishExact?: boolean;
  highlightColors?: (string | undefined)[] | null;
  visibleSectionNumbers?: string[] | undefined;
  onTocLoaded?: ((entries: TOCEntry[]) => void) | undefined;
}

const BilingualPdfViewerEmbedInner = forwardRef<
  BilingualPdfViewerHandle, BilingualPdfViewerEmbedProps & { config: BilingualViewerConfig }
>(
  function BilingualPdfViewerEmbedInner({ config, ...props }, ref) {
    const innerRef = useRef<RegulatoryViewerHandle>(null);
    const [region, setRegion] = useState<JpHoverRegion | null>(null);
    // Keep the last hovered region visible after the cursor leaves, so the
    // reader can move to the panel to read — only replaced by the next hover.
    const onBlockHover = useCallback((r: JpHoverRegion | null) => {
      if (r) setRegion(r);
    }, []);

    // Active in-document search word, forwarded from the inner viewer's search
    // box. The page layer is Chinese and the side panel is English — the SAME
    // query must NOT be highlighted on both surfaces at once, because
    // translation is not occurrence-preserving: a Chinese term may appear N×
    // in the source but the condensed English translation fewer times, so
    // highlighting both shows mismatched counts that look broken. Instead we
    // AUTO-DETECT the query's script and highlight exactly ONE surface:
    //   • Chinese (Han) query → boxed directly on the Chinese page (a 1:1
    //     substring match — honest, no translation step).
    //   • English query → highlighted in the English panel only; the page is
    //     left un-boxed (we do not translate the term, which is the step that
    //     produces the mismatched counts).
    const [enTerm, setEnTerm] = useState<string | null>(null);
    const scriptRe = config.sourceScriptRe;
    const queryIsChinese = !!enTerm && !!scriptRe && scriptRe.test(enTerm);
    // Page highlight — a source-script query (zh), or ANY query for Latin sources.
    const pageHighlightTerms = enTerm && (queryIsChinese || !scriptRe) ? [enTerm.trim()] : null;
    // English-panel highlight — an English query (zh), or ANY query for Latin sources.
    // Keyword-search landing (?highlight=) on a Latin source: translate the
    // term BOTH ways once (both endpoints are cached server-side) so the page
    // boxes the original-language word and the panel marks the English one,
    // whichever language the user typed. Not for zh (see the count note above).
    const kwTerm = props.highlightTerm?.trim() || '';
    const [kwSrc, setKwSrc] = useState('');
    const [kwEn, setKwEn] = useState('');
    useEffect(() => {
      setKwSrc(''); setKwEn('');
      if (!kwTerm || scriptRe) return undefined;
      let alive = true;
      const clean = (t: string) => t.replace(/<[^>]+>/g, '').trim().replace(/[.;:]+$/, '');
      config.translateTerm?.(kwTerm, props.docId)
        .then((t) => { if (alive) setKwSrc(clean(t || '')); }).catch(() => { /* page box skipped */ });
      config.translate(kwTerm, false, props.docId)
        .then((t) => { if (alive) setKwEn(clean(t || '')); }).catch(() => { /* typed term still marked */ });
      return () => { alive = false; };
    }, [kwTerm, props.docId]);   // eslint-disable-line react-hooks/exhaustive-deps
    const kwPanel = kwTerm ? [kwTerm, kwEn].filter(Boolean) : null;
    const kwPage = kwTerm ? [kwTerm, kwSrc].filter(Boolean) : null;
    const panelExact = !!props.englishExact && !enTerm;
    const panelHighlight = enTerm && !queryIsChinese ? enTerm : panelExact ? (props.highlightTerms?.length ? props.highlightTerms : kwTerm ? [kwTerm] : null) : kwPanel;

    // Canonical section-heading titles, keyed by their normalized Chinese
    // heading.  When the hovered block IS a section heading, we show this
    // stored ``title`` (the exact string the Contents TOC renders) instead
    // of the panel's independent live re-translation — so the TOC header and
    // the third-panel header never read differently for the same heading.
    // Body blocks are unaffected (they aren't headings, so they miss the map
    // and keep their live translation).
    const [headingTitleByZh, setHeadingTitleByZh] = useState<Map<string, string>>(new Map());
    useEffect(() => {
      let cancelled = false;
      config.viewerApi.fetchTOC(props.docId).then((toc) => {
        if (cancelled) return;
        const m = new Map<string, string>();
        for (const s of toc.sections ?? []) {
          const zh = ((s as any)[config.titleSrcField] ?? '').replace(/\s+/g, '');
          if (zh) m.set(zh, s.title);
        }
        setHeadingTitleByZh(m);
      }).catch(() => { /* TOC fetch best-effort; panel still works */ });
      return () => { cancelled = true; };
    }, [props.docId, config.viewerApi, config.titleSrcField]);

    // Resolve the English to show for the hovered region: a section heading
    // maps to its canonical TOC title; everything else uses the live
    // translation.  Headings are short single-line 【…】/〔…〕 blocks, so the
    // whitespace-stripped source matches a TOC ``title_zh`` exactly.
    const resolveRegionEn = useCallback((r: JpHoverRegion): string | undefined => {
      if (r.isHtml) return r.en; // tables never carry a heading title
      const key = (r.ja ?? '').replace(/\s+/g, '');
      const canonical = headingTitleByZh.get(key);
      return canonical ?? r.en;
    }, [headingTitleByZh]);

    // Resizable English panel — drag the left edge. Clamped 280–760px.
    const [panelW, setPanelW] = useState(384);
    const dragging = useRef(false);
    // Delta-based drag: snapshot the cursor x and panel width at mousedown,
    // then resize by the cursor's MOVEMENT, not by an absolute
    // cursor-to-right-edge measurement (layout-independent so it works on any
    // screen and in both directions).
    const dragStartX = useRef(0);
    const dragStartW = useRef(384);
    const containerRef = useRef<HTMLDivElement>(null);
    useEffect(() => {
      const onMove = (e: MouseEvent) => {
        if (!dragging.current) return;
        // Dragging LEFT (clientX decreases) widens the panel.
        const delta = dragStartX.current - e.clientX;
        setPanelW(Math.min(760, Math.max(280, dragStartW.current + delta)));
      };
      const onUp = () => {
        dragging.current = false;
        document.body.style.userSelect = '';
      };
      window.addEventListener('mousemove', onMove);
      window.addEventListener('mouseup', onUp);
      return () => {
        window.removeEventListener('mousemove', onMove);
        window.removeEventListener('mouseup', onUp);
      };
    }, []);

    useImperativeHandle(ref, () => ({
      scrollToPage: (n) => innerRef.current?.scrollToPage(n),
      highlightChunks: (ids) => innerRef.current?.highlightChunks(ids),
      scrollToSection: (s) => innerRef.current?.scrollToSection(s) ?? false,
    }));

    return (
      <div ref={containerRef} className="flex h-full min-h-0">
        <style>{`
          .zh-pdf-table table { border-collapse: collapse; width: 100%; font-size: 0.78rem; }
          .zh-pdf-table th, .zh-pdf-table td {
            border: 1px solid #e2e8f0; padding: 3px 6px; text-align: left; vertical-align: top;
          }
          .zh-pdf-table th { background: #f8fafc; font-weight: 600; }
        `}</style>
        <div className="flex-1 min-h-0">
          <RegulatoryViewerEmbed
            ref={innerRef}
            viewerApi={config.viewerApi}
            blockHoverTranslate={(src, preserveHtml, sectionId) =>
              config.translate(src, preserveHtml ?? false, props.docId, sectionId)}
            onBlockHover={onBlockHover}
            {...props}
            englishExact={false}
            onActiveSearchTermChange={setEnTerm}
            // In-document search classifies the query by SCRIPT: a Han query
            // searches the Chinese page text 1:1 (client-side); an English
            // query hits the translated server search. Han-only — NO kana — so
            // Japanese never routes into the zh page search.
            {...(config.sourceScriptRe ? { cjkScriptRe: config.sourceScriptRe } : {})}
            docSearch={config.docSearch}
            {...(config.translateTerm
              ? { translateSearchTerm: (en: string) => config.translateTerm!(en, props.docId) } : {})}
            // Scanned pages carry the box-OCR source text in this region field.
            {...(config.regionTextField ? { cjkRegionTextField: config.regionTextField } : {})}
            // Box the searched word on the page ONLY when the user typed
            // Chinese (the page text layer is Chinese, so it's a direct 1:1
            // match). English queries leave the page un-boxed and highlight the
            // English panel instead — see queryIsChinese above.
            // Compare-view keyword terms (props.highlightTerms) stay boxed; the
            // in-doc search term is appended (RegulatoryViewerEmbed also merges
            // its own sidebar search term).
            highlightTerms={pageHighlightTerms || props.highlightTerms || kwPage
              ? [...(props.highlightTerms ?? []), ...(kwPage ?? []), ...(pageHighlightTerms ?? [])] : null}
          />
        </div>

        {/* Drag handle to resize the English panel */}
        <div
          onMouseDown={(e) => {
            dragging.current = true;
            dragStartX.current = e.clientX;
            dragStartW.current = panelW;
            document.body.style.userSelect = 'none';
            e.preventDefault();
          }}
          className="w-1.5 flex-shrink-0 cursor-col-resize bg-slate-200 hover:bg-indigo-300 transition-colors"
          title="Drag to resize"
        />

        {/* English side panel — updates on hover, never covers the page */}
        <aside
          style={{ width: panelW }}
          className="flex-shrink-0 border-l border-slate-200 bg-white flex flex-col min-h-0">
          <div className="px-3 py-2 border-b border-slate-100 text-[11px] uppercase tracking-wide text-slate-400 flex items-center justify-between">
            <span>English</span>
            <span className="text-slate-300 normal-case">hover the {config.sourceName} text</span>
          </div>
          {enTerm && (
            <div className="px-3 py-1.5 text-[11px] normal-case text-slate-500 bg-slate-50 border-b border-slate-100">
              {!scriptRe
                ? <>Highlighted on the page (source) and here (English)</>
                : queryIsChinese
                  ? <>{config.sourceName} query — highlighted on the page</>
                  : <>English query — highlighted here, not on the page (counts differ)</>}
            </div>
          )}
          <div className="flex-1 min-h-0 overflow-auto p-3">
            {region == null ? (
              <div className="text-sm text-slate-400 italic mt-6 text-center">
                Hover any {config.sourceName} paragraph on the left to see its English here.
              </div>
            ) : (
              <div>
                {region.kind === 'table' && (
                  <div className="text-[10px] uppercase tracking-wide text-emerald-600 mb-1">Table</div>
                )}
                {region.en === undefined ? (
                  <div className="text-sm text-slate-400 italic">Translating…</div>
                ) : region.isHtml ? (
                  // Tables: linked translated + source grids with synced cell hover.
                  <LinkedTables enHtml={region.en || ''} zhHtml={region.ja} sourceLabel={config.sourceLabel} highlight={panelHighlight} englishExact={panelExact} />
                ) : (
                  <>
                    <FormattedBlock text={resolveRegionEn(region) || '(no translation)'} highlight={panelHighlight} englishExact={panelExact} />
                    <details className="mt-3">
                      <summary className="text-[11px] text-slate-400 cursor-pointer">{config.sourceLabel}</summary>
                      <div className="mt-1 text-xs text-slate-500">
                        <FormattedBlock text={region.ja} />
                      </div>
                    </details>
                  </>
                )}
              </div>
            )}
          </div>
        </aside>
      </div>
    );
  },
);

/** One instance per doc identity: the hover panel (last hovered region) and the
 *  heading map belong to ONE doc — see RegulatoryViewerEmbed. */
const BilingualPdfViewerEmbed = forwardRef<
  BilingualPdfViewerHandle, BilingualPdfViewerEmbedProps & { config: BilingualViewerConfig }
>(function BilingualPdfViewerEmbed(props, ref) {
  return <BilingualPdfViewerEmbedInner key={`${props.config.viewerApi.prefix}:${props.docId}`} ref={ref} {...props} />;
});

export default BilingualPdfViewerEmbed;
